import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Tag, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage,
  FormDescription 
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { Note, NoteCreate, NoteUpdate, NoteType, getNoteTypeLabel, EntityType } from '@/app/types/note';
import { useState } from 'react';
import { cn } from '@/lib/utils';

// Zod schema for note form
const formSchema = z.object({
  title: z.string().max(255, 'Title must be 255 characters or less').optional(),
  content: z.string().min(1, 'Note content is required').max(5000, 'Note must be 5000 characters or less'),
  note_type: z.nativeEnum(NoteType),
  is_pinned: z.boolean().default(false),
  is_private: z.boolean().default(false),
  tags: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high', 'critical']).optional(),
  due_date: z.string().optional(),
});

type NoteFormValues = z.infer<typeof formSchema>;

interface NoteFormProps {
  entityType: EntityType;
  entityId: number;
  note?: Note | null;
  onSubmit: (data: NoteCreate | NoteUpdate) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

export default function NoteForm({
  entityType,
  entityId,
  note,
  onSubmit,
  onCancel,
  isLoading = false,
  mode
}: NoteFormProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>(
    note?.extra_data?.tags || []
  );

  const form = useForm<NoteFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: note?.title || '',
      content: note?.content || '',
      note_type: note?.note_type || NoteType.GENERAL,
      is_pinned: note?.is_pinned || false,
      is_private: note?.is_private || false,
      tags: note?.extra_data?.tags?.join(', ') || '',
      priority: note?.extra_data?.priority || undefined,
      due_date: note?.extra_data?.due_date || '',
    },
  });

  const handleSubmit: SubmitHandler<NoteFormValues> = (values) => {
    // Build extra_data object if needed
    const extraData: any = {};
    
    if (tags.length > 0) {
      extraData.tags = tags;
    }
    
    if (values.priority) {
      extraData.priority = values.priority;
    }
    
    if (values.due_date) {
      extraData.due_date = values.due_date;
    }

    if (mode === 'create') {
      const createData: NoteCreate = {
        entity_type: entityType,
        entity_id: entityId,
        title: values.title || undefined,
        content: values.content,
        note_type: values.note_type,
        is_pinned: values.is_pinned,
        is_private: values.is_private,
        extra_data: Object.keys(extraData).length > 0 ? extraData : undefined,
      };
      onSubmit(createData);
    } else {
      const updateData: NoteUpdate = {
        title: values.title || undefined,
        content: values.content,
        note_type: values.note_type,
        is_pinned: values.is_pinned,
        is_private: values.is_private,
        extra_data: Object.keys(extraData).length > 0 ? extraData : undefined,
      };
      onSubmit(updateData);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        {/* Title (Optional) */}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title (Optional)</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  value={field.value || ''} 
                  placeholder="Give your note a title..."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Content */}
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Note Content*</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Write your note here..."
                  rows={5}
                  className="resize-y"
                />
              </FormControl>
              <FormDescription>
                {field.value.length}/5000 characters
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Note Type and Quick Options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Note Type */}
          <FormField
            control={form.control}
            name="note_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Note Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select note type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(NoteType).map((type) => (
                      <SelectItem key={type} value={type}>
                        {getNoteTypeLabel(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Quick Options */}
          <div className="space-y-3 pt-7">
            <FormField
              control={form.control}
              name="is_pinned"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal">
                      Pin to top
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_private"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal">
                      Private (only visible to you)
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Advanced Options (Collapsible) */}
        <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
          <CollapsibleTrigger asChild>
            <Button 
              type="button" 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground"
            >
              <ChevronDown className={cn(
                "h-4 w-4 mr-2 transition-transform",
                showAdvanced && "rotate-180"
              )} />
              Advanced Options
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-4">
            {/* Tags */}
            <div className="space-y-2">
              <FormLabel>Tags</FormLabel>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add tags..."
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="secondary" 
                  size="sm"
                  onClick={handleAddTag}
                >
                  <Tag className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 hover:text-destructive"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Priority */}
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Due Date */}
            <FormField
              control={form.control}
              name="due_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Calendar className="h-4 w-4 inline mr-2" />
                    Due Date (for reminders)
                  </FormLabel>
                  <FormControl>
                    <Input 
                      type="date" 
                      {...field} 
                      value={field.value || ''} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Add Note' : 'Update Note'}
          </Button>
        </div>
      </form>
    </Form>
  );
}